import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { crewStore } from '@/stores/CrewStore'
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import type { Crew } from '@/types'

const CrewsPage = observer(() => {
  const navigate = useNavigate()

  useEffect(() => {
    crewStore.clearFilters()
    crewStore.fetchCrews()
  }, [])

  const getProcessTypeBadgeVariant = (processType: string) => {
    const variants: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      sequential: 'primary',
      parallel: 'success',
      hierarchical: 'warning',
    }
    return variants[processType] || 'gray'
  }

  const getDomainBadgeVariant = (domain: string) => {
    const variants: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      analysis: 'primary',
      knowledge: 'success',
      automation: 'warning',
      planning: 'error',
    }
    return variants[domain] || 'gray'
  }

  const handleExecuteCrew = (crew: Crew) => {
    navigate(`/crews/${crew.name}/execute`)
  }

  const handleViewCrew = (crew: Crew) => {
    navigate(`/crews/${crew.name}`)
  }

  const handleToggleEnabled = async (crew: Crew) => {
    try {
      if (crew.enabled) {
        await crewStore.disableCrew(crew.name)
      } else {
        await crewStore.enableCrew(crew.name)
      }
    } catch (error) {
      console.error('Failed to toggle crew status:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Crews</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Multi-agent teams for collaborative task execution ({crewStore.crews.length} total, {crewStore.enabledCrewCount} enabled)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="primary" size="sm">
            {crewStore.totalAgentCount} total agents
          </Badge>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search crews..."
                  value={crewStore.searchQuery}
                  onChange={(e) => crewStore.setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => crewStore.clearFilters()}
              disabled={!crewStore.hasActiveFilters}
              className="whitespace-nowrap"
            >
              <FunnelIcon className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Clear Filters</span>
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <select
              value={crewStore.domainFilter}
              onChange={(e) => crewStore.setDomainFilter(e.target.value)}
              className="flex-1 px-3 sm:px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">All Domains</option>
              {crewStore.domains.map((domain) => (
                <option key={domain} value={domain}>
                  {domain.charAt(0).toUpperCase() + domain.slice(1)}
                </option>
              ))}
            </select>

            <select
              value={crewStore.processTypeFilter}
              onChange={(e) => crewStore.setProcessTypeFilter(e.target.value as any)}
              className="flex-1 px-3 sm:px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">All Process Types</option>
              <option value="sequential">Sequential</option>
              <option value="parallel">Parallel</option>
              <option value="hierarchical">Hierarchical</option>
            </select>

            <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={crewStore.showDisabledCrews}
                onChange={(e) => crewStore.setShowDisabledCrews(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-border rounded focus:ring-primary-500"
              />
              <span className="text-sm text-foreground whitespace-nowrap">Show disabled</span>
            </label>
          </div>
        </div>
      </Card>

      {/* Crew List */}
      {crewStore.isLoading ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading crews...</p>
        </Card>
      ) : crewStore.error ? (
        <Card className="text-center py-12">
          <p className="text-red-600">{crewStore.error}</p>
          <Button
            variant="secondary"
            onClick={() => crewStore.fetchCrews()}
            className="mt-4"
          >
            Retry
          </Button>
        </Card>
      ) : crewStore.filteredCrews.length === 0 ? (
        <Card className="text-center py-12">
          <UserGroupIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {crewStore.hasActiveFilters ? 'No crews match your filters' : 'No crews available'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {crewStore.hasActiveFilters
              ? 'Try adjusting your search or filters'
              : 'Crews are pre-configured multi-agent teams'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {crewStore.filteredCrews.map((crew) => (
            <Card
              key={crew.name}
              className={`hover:shadow-lg transition-shadow ${!crew.enabled ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${crew.enabled ? 'bg-primary-500/20' : 'bg-muted'}`}>
                    <UserGroupIcon className={`w-6 h-6 ${crew.enabled ? 'text-primary-600' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{crew.display_name}</h3>
                    <p className="text-xs text-muted-foreground">v{crew.version}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleEnabled(crew)}
                  className="p-1 rounded hover:bg-muted"
                  title={crew.enabled ? 'Disable crew' : 'Enable crew'}
                >
                  {crew.enabled ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {crew.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Domain:</span>
                  <Badge variant={getDomainBadgeVariant(crew.domain)} size="sm">
                    {crew.domain}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Process:</span>
                  <Badge variant={getProcessTypeBadgeVariant(crew.process_type)} size="sm">
                    {crew.process_type}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Agents:</span>
                  <span className="text-foreground font-medium">
                    {crew.agents.length} member{crew.agents.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Agent List Preview */}
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Team Members:</p>
                <div className="flex flex-wrap gap-1">
                  {crew.agents.slice(0, 3).map((agent, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-2 py-0.5 text-xs bg-muted text-foreground rounded"
                      title={agent.role}
                    >
                      {agent.name}
                    </span>
                  ))}
                  {crew.agents.length > 3 && (
                    <span className="inline-block px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded">
                      +{crew.agents.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-border">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleExecuteCrew(crew)}
                  className="flex-1"
                  disabled={!crew.enabled}
                >
                  <PlayIcon className="w-4 h-4 mr-1" />
                  Execute
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleViewCrew(crew)}
                >
                  <EyeIcon className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Error Display */}
      {crewStore.error && (
        <Card className="bg-red-500/10 border-red-500/20">
          <div className="flex items-center justify-between">
            <p className="text-red-600">{crewStore.error}</p>
            <Button variant="ghost" size="sm" onClick={() => crewStore.clearError()}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
})

export default CrewsPage
